import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  Shield, Smartphone, Key, Copy, Check, Loader2, AlertCircle,
  QrCode, RefreshCw, ShieldCheck, ShieldOff
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { api } from '../../lib/auth';
import { toast } from 'sonner';

export function MFASettings() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [setupData, setSetupData] = useState(null);
  const [verifyCode, setVerifyCode] = useState('');
  const [disableCode, setDisableCode] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [showRecoveryCodes, setShowRecoveryCodes] = useState(false);
  const [recoveryCodes, setRecoveryCodes] = useState([]);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    loadStatus();
  }, []);

  const loadStatus = async () => {
    try {
      const res = await api.get('/api/auth/mfa/status');
      setStatus(res.data);
    } catch (error) {
      toast.error('Failed to load MFA status');
    } finally {
      setLoading(false);
    }
  };

  const startSetup = async () => {
    setLoading(true);
    try {
      const res = await api.post('/api/auth/mfa/setup');
      setSetupData(res.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start MFA setup');
    } finally {
      setLoading(false);
    }
  };

  const verifySetup = async () => {
    if (!verifyCode || verifyCode.length !== 6) {
      toast.error('Please enter a 6-digit code');
      return;
    }

    setVerifying(true);
    try {
      const res = await api.post('/api/auth/mfa/verify-setup', { code: verifyCode });
      setRecoveryCodes(res.data.recovery_codes);
      setShowRecoveryCodes(true);
      setSetupData(null);
      toast.success('MFA enabled successfully!');
      loadStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid code');
    } finally {
      setVerifying(false);
    }
  };

  const disableMFA = async () => {
    if (!disableCode || disableCode.length !== 6) {
      toast.error('Please enter your current 6-digit code');
      return;
    }

    setVerifying(true);
    try {
      await api.post('/api/auth/mfa/disable', { code: disableCode });
      toast.success('MFA disabled');
      setDisableCode('');
      loadStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid code');
    } finally {
      setVerifying(false);
    }
  };

  const copyRecoveryCodes = () => {
    const codesText = recoveryCodes.join('\n');
    navigator.clipboard.writeText(codesText);
    setCopied(true);
    toast.success('Recovery codes copied to clipboard');
    setTimeout(() => setCopied(false), 2000);
  };

  if (loading && !setupData) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-cyan-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-green-500 to-emerald-600 flex items-center justify-center">
          <Shield className="w-6 h-6 text-white" />
        </div>
        <div>
          <h2 className="text-xl font-bold text-white">Two-Factor Authentication</h2>
          <p className="text-sm text-gray-400">Add an extra layer of security to your account</p>
        </div>
      </div>

      {/* Status Card */}
      <div className={`p-4 rounded-lg border ${status?.mfa_enabled ? 'bg-emerald-900/20 border-emerald-500/30' : 'bg-yellow-900/20 border-yellow-500/30'}`}>
        <div className="flex items-center gap-3">
          {status?.mfa_enabled ? (
            <ShieldCheck className="w-6 h-6 text-emerald-400" />
          ) : (
            <ShieldOff className="w-6 h-6 text-yellow-400" />
          )}
          <div>
            <p className={`font-medium ${status?.mfa_enabled ? 'text-emerald-400' : 'text-yellow-400'}`}>
              {status?.mfa_enabled ? 'MFA Enabled' : 'MFA Not Enabled'}
            </p>
            <p className="text-sm text-gray-400">
              {status?.mfa_enabled 
                ? `${status?.recovery_codes_remaining} recovery codes remaining`
                : status?.mfa_required 
                  ? 'MFA is required for your account'
                  : 'Recommended for enhanced security'}
            </p>
          </div>
        </div>
      </div>

      {/* Recovery Codes Modal */}
      {showRecoveryCodes && recoveryCodes.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="p-6 bg-red-900/20 border border-red-500/30 rounded-lg"
        >
          <div className="flex items-center gap-2 mb-4">
            <AlertCircle className="w-5 h-5 text-red-400" />
            <h3 className="text-lg font-bold text-red-400">Save Your Recovery Codes</h3>
          </div>
          <p className="text-sm text-gray-300 mb-4">
            Save these codes in a secure place. Each code can only be used once to access your account if you lose your authenticator.
          </p>
          <div className="grid grid-cols-2 gap-2 mb-4">
            {recoveryCodes.map((code, i) => (
              <code key={i} className="px-3 py-2 bg-black/40 rounded text-green-400 font-mono text-sm">
                {code}
              </code>
            ))}
          </div>
          <div className="flex gap-2">
            <Button onClick={copyRecoveryCodes} variant="outline">
              {copied ? <Check className="w-4 h-4 mr-2" /> : <Copy className="w-4 h-4 mr-2" />}
              {copied ? 'Copied!' : 'Copy All'}
            </Button>
            <Button onClick={() => setShowRecoveryCodes(false)} className="bg-emerald-600 hover:bg-emerald-700">
              I've Saved These Codes
            </Button>
          </div>
        </motion.div>
      )}

      {/* Setup Flow */}
      {!status?.mfa_enabled && !setupData && (
        <Button onClick={startSetup} disabled={loading} className="bg-gradient-to-r from-green-500 to-emerald-600">
          {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Smartphone className="w-4 h-4 mr-2" />}
          Set Up MFA
        </Button>
      )}

      {/* QR Code Setup */}
      {setupData && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="space-y-6"
        >
          <div className="p-6 bg-white/5 border border-white/10 rounded-lg">
            <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
              <QrCode className="w-5 h-5" />
              Scan QR Code
            </h3>
            <div className="flex flex-col md:flex-row gap-6">
              <div className="flex-shrink-0">
                <img 
                  src={setupData.qr_code} 
                  alt="MFA QR Code" 
                  className="w-48 h-48 bg-white p-2 rounded-lg"
                />
              </div>
              <div className="flex-1 space-y-4">
                <p className="text-sm text-gray-300">
                  1. Install an authenticator app like <strong>Google Authenticator</strong> or <strong>Authy</strong>
                </p>
                <p className="text-sm text-gray-300">
                  2. Scan the QR code with your authenticator app
                </p>
                <p className="text-sm text-gray-300">
                  3. Enter the 6-digit code from your app below
                </p>
                <div className="pt-2">
                  <p className="text-xs text-gray-500 mb-1">Manual entry key:</p>
                  <code className="px-3 py-2 bg-black/40 rounded text-cyan-400 font-mono text-sm block">
                    {setupData.manual_entry_key}
                  </code>
                </div>
              </div>
            </div>
          </div>

          <div className="flex gap-3">
            <Input
              type="text"
              placeholder="Enter 6-digit code"
              value={verifyCode}
              onChange={(e) => setVerifyCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              className="w-48 font-mono text-lg tracking-widest"
              maxLength={6}
            />
            <Button 
              onClick={verifySetup} 
              disabled={verifying || verifyCode.length !== 6}
              className="bg-gradient-to-r from-green-500 to-emerald-600"
            >
              {verifying ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Check className="w-4 h-4 mr-2" />}
              Verify & Enable
            </Button>
            <Button variant="outline" onClick={() => setSetupData(null)}>
              Cancel
            </Button>
          </div>
        </motion.div>
      )}

      {/* Disable MFA */}
      {status?.mfa_enabled && (
        <div className="p-6 bg-white/5 border border-white/10 rounded-lg">
          <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <ShieldOff className="w-5 h-5 text-red-400" />
            Disable MFA
          </h3>
          <p className="text-sm text-gray-400 mb-4">
            Enter your current authenticator code to disable MFA. This will make your account less secure.
          </p>
          <div className="flex gap-3">
            <Input
              type="text"
              placeholder="Enter 6-digit code"
              value={disableCode}
              onChange={(e) => setDisableCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              className="w-48 font-mono text-lg tracking-widest"
              maxLength={6}
            />
            <Button 
              onClick={disableMFA} 
              disabled={verifying || disableCode.length !== 6}
              variant="destructive"
            >
              {verifying ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Disable MFA
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

export default MFASettings;
